import { 
  Shield, 
  Award, 
  Crown, 
  Star, 
  Gem, 
  Trophy,
  Swords,
  Castle,
  Scroll
} from "lucide-react";

export type Gender = "male" | "female";

export interface StageLevel {
  level: number;
  name: string;
  maleName: string;
  femaleName: string;
  description: string;
  maleDescription: string;
  femaleDescription: string;
  colors: string;
  bgColor: string;
  borderColor: string;
  accentColor: string;
  icon: typeof Shield;
  tier: string;
}

// Executive stage progression - aligned with the developmental stages
// Stage 0 = Initiate (Onboarding), Stages 1-8 = Core progression
export const initiateStage: StageLevel = {
  level: 0,
  name: "Initiate",
  maleName: "Initiate",
  femaleName: "Initiate",
  description: "Reading the scroll of formation",
  maleDescription: "Reading the scroll of formation",
  femaleDescription: "Reading the scroll of formation",
  colors: "from-amber-900/60 to-amber-950/80",
  bgColor: "bg-amber-950/10",
  borderColor: "border-amber-900/30",
  accentColor: "text-amber-800",
  icon: Scroll,
  tier: "Onboarding"
};

export const stageLevels: StageLevel[] = [
  {
    level: 1, 
    name: "Encumbered",
    maleName: "Encumbered", 
    femaleName: "Encumbered",
    description: "Carrying the weight of the old life",
    maleDescription: "Carrying the weight of the old life",
    femaleDescription: "Finding stable ground",
    colors: "from-amber-800/60 to-amber-900/80",
    bgColor: "bg-amber-950/10",
    borderColor: "border-amber-800/30",
    accentColor: "text-amber-700",
    icon: Shield,
    tier: "Foundation"
  },
  { 
    level: 2, 
    name: "Resolved",
    maleName: "Resolved", 
    femaleName: "Resolved",
    description: "Walking with purpose",
    maleDescription: "Walking with purpose",
    femaleDescription: "Covenant to change",
    colors: "from-amber-700 to-amber-800",
    bgColor: "bg-amber-950/10",
    borderColor: "border-amber-700/30",
    accentColor: "text-amber-600",
    icon: Award,
    tier: "Bronze Circle"
  },
  { 
    level: 3, 
    name: "Reformed",
    maleName: "Reformed", 
    femaleName: "Reformed",
    description: "Standing upright and clean",
    maleDescription: "Standing upright and clean",
    femaleDescription: "Building new patterns",
    colors: "from-amber-600 to-red-900/40",
    bgColor: "bg-amber-950/10",
    borderColor: "border-amber-600/30",
    accentColor: "text-amber-600",
    icon: Star,
    tier: "Silver Circle"
  },
  { 
    level: 4, 
    name: "Cohortian",
    maleName: "Cohortian", 
    femaleName: "Cohortian",
    description: "Armored for the community",
    maleDescription: "Armored for the community",
    femaleDescription: "Power of the remnant",
    colors: "from-amber-500 to-red-800/60",
    bgColor: "bg-amber-950/10",
    borderColor: "border-amber-500/30",
    accentColor: "text-amber-600",
    icon: Swords,
    tier: "Gold Circle"
  },
  { 
    level: 5, 
    name: "Tempered",
    maleName: "Tempered", 
    femaleName: "Tempered",
    description: "Helmed and seasoned",
    maleDescription: "Helmed and seasoned",
    femaleDescription: "Forged through trials",
    colors: "from-red-800 to-amber-600",
    bgColor: "bg-amber-950/10",
    borderColor: "border-red-800/30",
    accentColor: "text-red-700",
    icon: Gem,
    tier: "Platinum Circle"
  },
  { 
    level: 6, 
    name: "Watchman",
    maleName: "Watchman", 
    femaleName: "Watchwoman",
    description: "Sword drawn, keeping watch",
    maleDescription: "Sword drawn, keeping watch",
    femaleDescription: "Protector of many",
    colors: "from-red-700 to-red-900",
    bgColor: "bg-amber-950/10",
    borderColor: "border-red-700/30",
    accentColor: "text-red-700",
    icon: Trophy,
    tier: "Diamond Circle"
  },
  { 
    level: 7, 
    name: "Vanguard",
    maleName: "Vanguard", 
    femaleName: "Vanguard",
    description: "Sword raised, leading from the front",
    maleDescription: "Sword raised, leading from the front",
    femaleDescription: "Leading with courage",
    colors: "from-red-600 to-red-800",
    bgColor: "bg-amber-950/10",
    borderColor: "border-amber-500/30",
    accentColor: "text-amber-500",
    icon: Crown,
    tier: "Executive Circle"
  },
  { 
    level: 8, 
    name: "Mighty Man & Radiant Woman",
    maleName: "Mighty Man", 
    femaleName: "Radiant Woman",
    description: "Kneeling in gold before the King",
    maleDescription: "Kneeling in gold before the King",
    femaleDescription: "Her worth is far above rubies",
    colors: "from-red-700 to-amber-500",
    bgColor: "bg-gradient-to-br from-amber-950/10 to-gold/10",
    borderColor: "border-gold",
    accentColor: "text-gold",
    icon: Castle,
    tier: "Chairman's Circle"
  },
];

// Stages 1-8 for the avatar gallery (Initiate retired: buying the book is the initiation)
export const allStageLevels: StageLevel[] = [...stageLevels];

// Get display data based on gender
export function getStageForGender(stage: StageLevel, gender: Gender) {
  return {
    level: stage.level,
    name: gender === "male" ? stage.maleName : stage.femaleName,
    description: gender === "male" ? stage.maleDescription : stage.femaleDescription,
    colors: stage.colors,
    bgColor: stage.bgColor,
    borderColor: stage.borderColor,
    accentColor: stage.accentColor,
    icon: stage.icon,
    tier: stage.tier,
  };
}

// Stage names by gender
export const stageNames = {
  male: [
    "Encumbered",
    "Resolved",
    "Reformed",
    "Cohortian",
    "Tempered",
    "Watchman",
    "Vanguard",
    "Mighty Man",
  ],
  female: [
    "Encumbered",
    "Resolved",
    "Reformed",
    "Cohortian",
    "Tempered",
    "Watchwoman",
    "Vanguard",
    "Radiant Woman",
  ],
};

// Stage descriptions by gender
export const stageDescriptions = {
  male: [
    "Carrying the weight of the old life",
    "Walking with purpose",
    "Standing upright and clean",
    "Armored for the community",
    "Helmed and seasoned",
    "Sword drawn, keeping watch",
    "Sword raised, leading from the front",
    "Kneeling in gold before the King",
  ],
  female: [
    "Finding stable ground to stand",
    "Drawing the line, making a covenant",
    "Producing fruit in keeping with repentance",
    "Marching in formation with the remnant",
    "Refined through trials like gold in fire",
    "Appointed as a watchwoman for the people",
    "Going forth with courage and faith",
    "Her worth is far above rubies - Proverbs 31",
  ],
};

// Tier names for display
export const tierNames = [
  "Foundation",
  "Bronze Circle",
  "Silver Circle",
  "Gold Circle",
  "Platinum Circle",
  "Diamond Circle",
  "Executive Circle",
  "Chairman's Circle",
];

// Legacy exports for backwards compatibility
export const warriorLevels = stageLevels;
export type WarriorLevel = StageLevel;
export function getWarriorForGender(warrior: StageLevel, gender: Gender) {
  return getStageForGender(warrior, gender);
}
